import { createFileRoute } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useMemo, useState } from "react";
import { PageHeader, Toolbar, DataCard, StatusPill } from "@/components/admin/AdminPrimitives";
import { Skeleton } from "@/components/ui/skeleton";

export const Route = createFileRoute("/_authenticated/admin/pedidos")({
  head: () => ({ meta: [{ title: "Pedidos — Admin" }, { name: "robots", content: "noindex, nofollow" }] }),
  component: PedidosAdmin,
});

function fmtBRL(cents: number) {
  return new Intl.NumberFormat("pt-BR", { style: "currency", currency: "BRL" }).format(cents / 100);
}

const TONE: Record<string, any> = { paid: "green", pending: "gold", failed: "red", refunded: "gray", canceled: "gray" };

function PedidosAdmin() {
  const [q, setQ] = useState("");
  const list = useQuery({
    queryKey: ["admin.pedidos"],
    queryFn: async () => {
      const { data } = await supabase
        .from("orders")
        .select("id, status, total_cents, provider, external_ref, created_at, paid_at, user_id")
        .order("created_at", { ascending: false })
        .limit(500);
      return data ?? [];
    },
  });
  const rows = useMemo(() => {
    const s = q.trim().toLowerCase();
    if (!s) return list.data ?? [];
    return (list.data ?? []).filter((r: any) =>
      [r.id, r.external_ref, r.provider].filter(Boolean).some((v) => String(v).toLowerCase().includes(s)),
    );
  }, [q, list.data]);

  return (
    <div>
      <PageHeader title="Pedidos" description="Histórico completo de compras e pagamentos." />
      <Toolbar search={q} onSearch={setQ} placeholder="ID, referência externa, gateway…" />
      <DataCard>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-[var(--gray-soft)]/60 text-left text-[11px] uppercase tracking-wide text-[var(--gray-ink)]">
              <tr>
                <th className="px-4 py-3 font-semibold">Pedido</th>
                <th className="px-4 py-3 font-semibold">Gateway</th>
                <th className="px-4 py-3 font-semibold">Status</th>
                <th className="px-4 py-3 text-right font-semibold">Valor</th>
                <th className="px-4 py-3 font-semibold">Criado</th>
                <th className="px-4 py-3 font-semibold">Pago</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-black/[0.05]">
              {list.isLoading ? (
                Array.from({ length: 8 }).map((_, i) => (
                  <tr key={i}><td colSpan={6} className="p-2"><Skeleton className="h-9 w-full" /></td></tr>
                ))
              ) : rows.length === 0 ? (
                <tr><td colSpan={6} className="py-16 text-center text-sm text-[var(--gray-ink)]">Nenhum pedido encontrado.</td></tr>
              ) : (
                rows.map((o: any) => (
                  <tr key={o.id} className="transition hover:bg-[var(--gray-soft)]/40">
                    <td className="px-4 py-3 font-mono text-xs">
                      <p className="text-[var(--navy)]">{o.id.slice(0, 8)}</p>
                      <p className="text-[10px] text-[var(--gray-ink)]">{o.external_ref ?? "—"}</p>
                    </td>
                    <td className="px-4 py-3 text-[var(--gray-ink)]">{o.provider ?? "—"}</td>
                    <td className="px-4 py-3">
                      <StatusPill tone={TONE[o.status] ?? "gray"}>{o.status}</StatusPill>
                    </td>
                    <td className="px-4 py-3 text-right font-semibold">{fmtBRL(o.total_cents)}</td>
                    <td className="px-4 py-3 text-xs text-[var(--gray-ink)]">
                      {new Date(o.created_at).toLocaleString("pt-BR", { dateStyle: "short", timeStyle: "short" })}
                    </td>
                    <td className="px-4 py-3 text-xs text-[var(--gray-ink)]">
                      {o.paid_at ? new Date(o.paid_at).toLocaleString("pt-BR", { dateStyle: "short", timeStyle: "short" }) : "—"}
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </DataCard>
    </div>
  );
}
